import React, { memo, useCallback, useContext, useMemo } from "react";
import { format, isEmpty } from "@swiggy-private/js-utils";
import { PLWidgetController, WidgetControllerContext } from "@swiggy-private/dls-react-mxn";
import ElementObserver from "@swiggy-private/dls-react-mxn/dist/ElementObserver";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { Svg16 as Back16 } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronLeft";
import { SearchBar } from "@swiggy-private/dls-web-recipe-common-web/dist/components/SearchBar";
import {
    ResponseContext,
    ScreenNames,
    SwiggyContextHeader,
} from "@swiggy-private/portal-dineout-context";
import { ICartItems } from "@swiggy-private/portal-dineout-context";
import { TEXTS } from "./constants";
import {
    FloatingWidgetContainer,
    SearchBarWrapper,
    SearchContainer,
    StyledText,
    Wrapper,
} from "./styles";
import ISearchPageProps from "./types";
import { OBJECT_NAMES, OBJECT_VALUES, goToCartWidgetAnalyticsInfo } from "../constants/analytics";
import { FloatingWidget } from "../FloatingWidget";
import { useAnalytics } from "../hooks";

const SearchPage = ({
    showResults,
    query,
    searchResultCards,
    genericClickHandler,
    handleSearch,
    ifCartItemAvailable,
    goToCart,
    goBack,
    onEmptyPage,
    onWidgetError,
    onInvalidWidget,
    impressionAnalytics: defaultImpressionAnalytics,
    clickAnalytics: defaultClickAnalytics,
}: ISearchPageProps) => {
    const { state } = useContext(ResponseContext);
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { impressionAnalytics, clickAnalytics } = useAnalytics({
        screenName: ScreenNames.SEARCH,
        defaultImpressionAnalytics,
        defaultClickAnalytics,
    });

    const cartItemsCount = useMemo(
        () =>
            state.cart?.localCart?.cartItems?.reduce(
                (total: number, item: ICartItems) => total + item.quantity,
                0
            ),
        [state.cart?.localCart?.cartItems]
    );

    const contextValue = useMemo(
        () => ({
            impressionAnalytics,
            clickAnalytics,
            clickHandler: genericClickHandler,
            swiggyHeaders,
        }),
        [clickAnalytics, impressionAnalytics, genericClickHandler, swiggyHeaders]
    );

    const leftIcon = (
        <GenericIcon Icon={Back16} fillColor="text_Highest_Emphasis" onClick={goBack} />
    );

    const cardData = useMemo(() => {
        if (!showResults) {
            return <></>;
        }

        if (!isEmpty(searchResultCards)) {
            return (
                <WidgetControllerContext.Provider value={contextValue}>
                    <Wrapper>
                        <PLWidgetController
                            onInvalidWidget={onInvalidWidget}
                            onWidgetError={onWidgetError}
                            cards={searchResultCards}
                            onEmptyPage={onEmptyPage}
                        />
                    </Wrapper>
                </WidgetControllerContext.Provider>
            );
        } else {
            return (
                <StyledText variant="V2.body2.neutral.medium" color="text_Highest_Emphasis">
                    {format(TEXTS.NOT_FOUND, query)}
                </StyledText>
            );
        }
    }, [
        contextValue,
        onEmptyPage,
        onInvalidWidget,
        onWidgetError,
        searchResultCards,
        query,
        showResults,
    ]);

    const goToCartImpression = useCallback(() => {
        impressionAnalytics?.({
            ...goToCartWidgetAnalyticsInfo,
            category: ScreenNames.SEARCH,
            action: OBJECT_VALUES.GO_TO_CART_IMPRESSION_CONTAINER,
            context: JSON.stringify({
                item_count: cartItemsCount,
            }),
        });
    }, [impressionAnalytics, cartItemsCount]);

    const goToCartClick = useCallback(() => {
        clickAnalytics?.({
            ...goToCartWidgetAnalyticsInfo,
            category: ScreenNames.SEARCH,
            action: OBJECT_VALUES.GO_TO_CART_CLICK_CONTAINER,
            context: JSON.stringify({
                item_count: cartItemsCount,
            }),
        });
    }, [clickAnalytics, cartItemsCount]);

    const onSearchBarImpression = useCallback(() => {
        impressionAnalytics?.({
            category: ScreenNames.SEARCH,
            action: OBJECT_NAMES.SEARCH_BAR_IMPRESSION,
        });
    }, [impressionAnalytics]);

    const onSearchQueryChange = useCallback(
        (text: string) => {
            clickAnalytics?.({
                category: ScreenNames.SEARCH,
                action: OBJECT_NAMES.SEARCH_BAR_QUERY_CHANGE,
                context: JSON.stringify({
                    query: text,
                }),
            });
            handleSearch(text);
        },
        [clickAnalytics, handleSearch]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (e.key === "Enter") {
                e.preventDefault(); // Prevents moving to a new line
            }
        },
        []
    );

    return (
        <SearchContainer>
            <ElementObserver onViewEnter={onSearchBarImpression} senseOnce>
                <SearchBarWrapper>
                    <SearchBar
                        onSearch={onSearchQueryChange}
                        placeholder="Search for food, drinks "
                        leftIcon={leftIcon}
                        showTextAreaIcon={true}
                        onKeyDown={handleKeyDown}
                        autoFocus={true}
                    />
                </SearchBarWrapper>
            </ElementObserver>

            {cardData}

            <FloatingWidgetContainer>
                <FloatingWidget
                    visible={ifCartItemAvailable}
                    rightContent={{ text: "Go to cart" }}
                    handler={goToCart}
                    leftContent={{
                        text: cartItemsCount > 0 ? `${cartItemsCount} item(s)` : "",
                    }}
                    impressionAnalytics={goToCartImpression}
                    clickAnalytics={goToCartClick}
                />
            </FloatingWidgetContainer>
        </SearchContainer>
    );
};

export default memo(SearchPage);
